import NoDataIcon from "@assets/icons/new/no_data.svg?react";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { MemberProfileEmptyPage } from "@components/common/empty-page/member-profile-empty-page";
import { MemberGroupsTable } from "@components/common/member/member-groups-table";
import { MemberProfileSkeleton } from "@components/common/skeleton-loading/member-profile-skeleton";
import { MemberProfileInfoBar } from "@components/member-profile/member-profile-info-bar";
import { UserInfo, userInfoWidth } from "@components/user-info/user-info";
import { isCompanyMemberDetails } from "@custom-types/member-type-guards";
import { BaseMemberProps } from "@custom-types/member-types";
import { PartialNull } from "@custom-types/types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useMemberGroups } from "@hooks/use-member-groups";
import { Box, Grid2, Stack } from "@mui/material";
import { TeamDetailsInfoBar } from "@pages/members/teams/team-details/team-details-info-bar";
import { NotFoundPage } from "@pages/not-found-page";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { UI_HEIGHTS_VIRTUALIZATION_GRID } from "@styles/common-styles";

interface Props extends PartialNull<BaseMemberProps> {
  /** Flag to indicate if the content of the member is loading */
  isLoading: boolean;

  /** Validate if we are opening a team overview or a member overview */
  subjectType: "team" | "member";
}

/**
 * Page to show the overview of the profile of the selected user.
 */
export function MemberOverviewPage({
  member,
  isLoading,
  subjectType,
}: Props): JSX.Element {
  const { groups, companyMemberGroupsStatus } = useMemberGroups();
  const { isFetchingTeamMembers } = useAppSelector(fetchingTeamsFlagsSelector);
  const { isScreenMdOrSmaller } = useMediaQueryList();

  const isFetching = isLoading || companyMemberGroupsStatus === "pending";

  if (isFetching || !groups) {
    return <MemberProfileSkeleton />;
  }

  // Show not found page if fetching member or groups failed
  // or if the member entity is not of type CompanyMemberDetails
  if (
    !member ||
    !isCompanyMemberDetails(member) ||
    companyMemberGroupsStatus !== "succeeded"
  ) {
    return <NotFoundPage />;
  }

  let mainContent;

  if (groups.length === 0) {
    switch (subjectType) {
      case "member":
        mainContent = <MemberProfileEmptyPage />;
        break;
      case "team":
        mainContent = (
          <EmptyPage
            title="No access granted"
            subtitle={`This ${TEAM_DISPLAY_NAME} hasn't been assigned to any groups or projects within the workspace`}
            icon={NoDataIcon}
          />
        );
        break;
      default:
        mainContent = (
          <EmptyPage
            title="Unexpected subject type"
            subtitle="Please contact support to resolve this issue."
            icon={NoDataIcon}
          />
        );
        break;
    }
  } else {
    mainContent = <MemberGroupsTable groups={groups} member={member} />;
  }

  // Calculate the max height for the main content area based on subject type and screen size
  const pageSpecificOffset =
    subjectType === "team" && isScreenMdOrSmaller
      ? UI_HEIGHTS_VIRTUALIZATION_GRID.memberOverviewPage.team.mediumScreen
      : UI_HEIGHTS_VIRTUALIZATION_GRID.memberOverviewPage.default;

  const topOffset =
    UI_HEIGHTS_VIRTUALIZATION_GRID.appBar +
    UI_HEIGHTS_VIRTUALIZATION_GRID.tabBar +
    pageSpecificOffset + UI_HEIGHTS_VIRTUALIZATION_GRID.memberOverviewPage.marginTop;

  const bottomOffset =
    UI_HEIGHTS_VIRTUALIZATION_GRID.contentPadding +
    UI_HEIGHTS_VIRTUALIZATION_GRID.border;

  return (
    <Grid2
      data-testid={
        subjectType === "member"
          ? "MEMBER_PROFILE_OVERVIEW_CONTAINER"
          : "TEAM_PROFILE_OVERVIEW_CONTAINER"
      }
    >
      {subjectType === "member" ? (
        <MemberProfileInfoBar role={member.role} />
      ) : (
        <TeamDetailsInfoBar isLoading={isFetchingTeamMembers === "pending"} />
      )}

      {/* Container for the projects, groups and user info component */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
          width: "100%",
          gap: subjectType === "member" ? "20px" : 0,
        }}
      >
        {/* Container for the projects and groups */}
        <Box
          component="div"
          marginTop={"14px"}
          sx={{
            width: {
              md: subjectType === "member" ? `calc(100% - ${userInfoWidth})` : "100%",
              sx: "100%",
            },
            maxHeight: {
              md: `calc(100vh - ${topOffset + bottomOffset}px)`,
              xs: "100%",
            },
            overflowY: "auto",
          }}
        >
          {mainContent}
        </Box>

        {/* Container for the user info */}
        {subjectType === "member" && (
          <Stack marginTop="14px" alignItems="center">
            <UserInfo member={member} />
          </Stack>
        )}
      </Box>
    </Grid2>
  );
}
